import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../../query';
import { useStudentApi } from '../../../api';
import { studentKeys } from '../../../query-key-factory';
import { ConsumerStudentPaymentSearch, ConsumerStudentPaymentsResponse } from './payments.types';

type GetStudentPaymentsListContext = {
  meta?: {
    studentId?: string;
    queryString?: string;
  };
};

const useGetConsumerStudentPaymentsList = () => {
  const StudentApi = useStudentApi();

  const getStudentPaymentsList = async (context: GetStudentPaymentsListContext) => {
    const { studentId, queryString } = context.meta || {};
    const { data } = await StudentApi.get<ConsumerStudentPaymentsResponse>(
      `/consumer/students/${studentId}/payments?${queryString}`
    );
    return data;
  };

  return getStudentPaymentsList;
};

interface UseListStudentPaymentsArgs<T> {
  studentId: string;
  select?: (data: ConsumerStudentPaymentsResponse) => T;
  queryOptions?: ConsumerStudentPaymentSearch;
  pagination?: QueryPagination;
}

/***
 * Gets all payments for a student by student id
 */
const useListConsumerStudentPayments = <T = ConsumerStudentPaymentsResponse>({
  studentId,
  select,
  queryOptions,
  pagination,
}: UseListStudentPaymentsArgs<T>) => {
  const getStudentPaymentsList = useGetConsumerStudentPaymentsList();

  const { queryString, queryObject, queryPagination } =
    createQueryOptions<ConsumerStudentPaymentSearch>({
      search: queryOptions,
      pagination,
    });

  const queryKey = useMemo(() => {
    if (!studentId) return [];
    const key = studentKeys.id('user').consumer.students.id(studentId).payments.all;

    return [...key, queryObject, queryPagination];
  }, [studentId, queryObject, queryPagination]);

  return useQuery({
    queryKey,
    queryFn: getStudentPaymentsList,
    meta: { studentId, queryString },
    enabled: !!studentId,
    select,
  });
};

export { useListConsumerStudentPayments };
